import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { asArray } from "./structuredAnswers";
import GrossZahlEingabe from "./GrossZahlEingabe";

/**
 * Gesamtfläche (xlsx Z. 7, Punkt 5).
 *
 * Die Spec sagt dort ausdrücklich „Gesamtfläche (**Verknüpfung zu Bereich
 * Einheiten**, dort werden Angaben gemacht)". Führende Größe ist damit die
 * **Summe der Einheitenflächen** — `gesamtflaeche` bleibt der gespeicherte
 * Answer-Key (Stammdaten, Vertragsmaske, Zusammenfassung lesen ihn), wird aber
 * aus den Einheiten gespeist.
 *
 * Geschrieben wird **ausschließlich auf Klick**. Ein Effekt, der die Summe beim
 * bloßen Rendern einträgt, hätte zwei Nebenwirkungen: im Backoffice meldete das
 * Fenster ungespeicherte Änderungen, obwohl niemand etwas angefasst hat
 * (`onAnswersChange` → `onDirtyChange(true)`), und beim nächsten Speichern
 * landete ein Wert in der Akte, den niemand eingegeben hat. Stattdessen steht
 * die Summe als Vorschlag da, den ein Klick übernimmt.
 *
 * Zwei Varianten, weil die Flächen **später** erfasst werden, als die Frage
 * gestellt wird (`spezifizierung` steht in der Schrittkette vor
 * `einheiten_detail`):
 *
 *  - `feld` — im Schritt „Bauart": das Eingabefeld samt Vorschlag/Abgleich,
 *    sobald Einheitenflächen vorliegen (Rückkehr in den Schritt, Edit-Modus).
 *  - `abgleich` — unter der Einheitenliste, also genau dort, wo die Flächen
 *    entstehen: kein zweites Eingabefeld, nur Summe, Abweichung und der Knopf.
 *    Ohne Einheitenflächen rendert diese Variante nichts.
 *
 * Bewusst **kein** stilles Überschreiben eines vorhandenen Werts: Flächen aus
 * Bestandsdaten und Sonderfälle (Verkehrsflächen, nicht erfasste Einheiten)
 * gingen sonst ohne Zutun verloren. Und bewusst kein zweiter Answer-Key für die
 * Summe — sie ist jederzeit aus `einheiten` neu berechenbar.
 */

const useStyles = makeStyles({
  block: {
    paddingTop: "20px",
    paddingBottom: "20px",
    ...shorthands.borderBottom("1px", "solid", BRAND.borderDashed),
  },
  frage: {
    marginTop: 0,
    marginBottom: "4px",
    fontFamily: BRAND.fontAccent,
    fontSize: "1.2rem",
    fontWeight: 400,
    lineHeight: 1.3,
    color: BRAND.ink,
    "@media (min-width: 768px)": { fontSize: "1.35rem" },
  },
  hinweis: {
    marginTop: 0,
    marginBottom: "12px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.muted,
  },
  summe: {
    marginTop: "10px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  abweichung: {
    marginTop: "10px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    backgroundColor: "#FFF8E8",
    ...shorthands.borderLeft("3px", "solid", BRAND.gold),
    ...shorthands.borderRadius("4px"),
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    color: BRAND.ink,
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    alignItems: "flex-start",
  },
  uebernehmen: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 700,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: "#f5eef6" },
  },
});

/** „120,5" und „120.5" gelten gleich; alles andere ist keine Fläche. */
const zahl = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  const t = v.trim().replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/** Auf zwei Nachkommastellen gerundet, ohne nachlaufende Nullen. */
const formatFlaeche = (n: number): string => String(Math.round(n * 100) / 100);

/**
 * Summe der Einheitenflächen. `null`, solange keine einzige Einheit eine Fläche
 * trägt — dann gibt es nichts zu verknüpfen und der Block verhält sich wie ein
 * gewöhnliches Zahlenfeld.
 */
export const summeEinheitenFlaechen = (einheiten: unknown): number | null => {
  let summe = 0;
  let gefunden = false;
  for (const einheit of asArray<unknown>(einheiten)) {
    if (typeof einheit !== "object" || einheit === null) continue;
    const n = zahl((einheit as Record<string, unknown>).flaeche);
    if (n == null) continue;
    summe += n;
    gefunden = true;
  }
  return gefunden ? summe : null;
};

type Props = {
  /** Rohwert des Answer-Keys `einheiten`. */
  einheiten: unknown;
  /** Rohwert des Answer-Keys `gesamtflaeche`. */
  value: unknown;
  onChange: (naechste: string) => void;
  /** Mit Eingabefeld (Schritt „Bauart") oder nur Abgleich (unter den Einheiten). */
  variante?: "feld" | "abgleich";
};

const GesamtflaecheBlock = ({
  einheiten,
  value,
  onChange,
  variante = "feld",
}: Props) => {
  const styles = useStyles();
  const text = typeof value === "number" ? String(value) : String(value ?? "");
  const summe = summeEinheitenFlaechen(einheiten);
  const summeText = summe == null ? "" : formatFlaeche(summe);
  const erfasst = zahl(text);
  const leer = text.trim() === "";
  // Rundungsreste aus „33.33 + 33.33 + 33.34" sollen keine Warnung auslösen.
  const abweichung =
    summe != null && erfasst != null ? Math.abs(erfasst - summe) : 0;
  const weichtAb = abweichung > 0.5;

  // Unter den Einheiten gibt es ohne Flächen nichts zu sagen — dort steht der
  // Block nur als Verknüpfung, nicht als eigene Frage.
  if (variante === "abgleich" && summe == null) return null;

  const uebernehmenKnopf = (
    <button
      type="button"
      className={styles.uebernehmen}
      onClick={() => onChange(summeText)}
    >
      Summe übernehmen ({summeText} m²)
    </button>
  );

  return (
    <section className={styles.block}>
      <h3 className={styles.frage}>Gesamtfläche des Gebäudes (m²)</h3>
      {variante === "abgleich" && (
        <p className={styles.hinweis}>
          Verknüpft mit dem Schritt „Bauart“: dort steht die Gesamtfläche des Gebäudes. Übernommen
          wird die Summe nur auf Klick.
        </p>
      )}
      {variante === "feld" && (
        // Große Ziffern mit farbigem Unterstrich statt Kasten-Feld. `dezimal`
        // ist hier Pflicht: Flächen tragen im Bestand Nachkommastellen
        // („120,5"), ein reiner Ziffernfilter verschluckte sie.
        <GrossZahlEingabe
          value={text}
          onChange={onChange}
          name="gesamtflaeche"
          ariaLabel="Gesamtfläche des Gebäudes in Quadratmetern"
          platzhalter="0"
          einheit="m²"
          dezimal
        />
      )}
      {summe != null && leer && (
        <div className={styles.abweichung}>
          <span>
            Die Einheiten ergeben zusammen {summeText} m²
            {variante === "abgleich"
              ? " — als Gesamtfläche ist noch nichts erfasst."
              : "."}
          </span>
          {uebernehmenKnopf}
        </div>
      )}
      {summe != null && !leer && weichtAb && (
        <div className={styles.abweichung}>
          <span>
            Die Einheiten ergeben zusammen {summeText} m², als Gesamtfläche
            stehen {formatFlaeche(erfasst ?? 0)} m² — Abweichung{" "}
            {formatFlaeche(abweichung)} m².
          </span>
          {uebernehmenKnopf}
        </div>
      )}
      {summe != null && !leer && !weichtAb && (
        <div className={styles.summe}>
          Summe der Einheitenflächen: {summeText} m² — das passt zur erfassten
          Gesamtfläche.
        </div>
      )}
    </section>
  );
};

export default GesamtflaecheBlock;
